import React, { useState, useEffect, useCallback } from 'react';
import API_BASE from '../../../utils/api.js';
import { authFetch } from '../../utils/authFetch';
import notify from '../../../utils/notify';
import '../../../component-styles/SendEmailModal.css';

const DEFAULTS = {
  primaryColor: '#a5d27d',
  buttonTextColor: '#1a1a1a',
};

export default function SendPurchaseOrderModal({
  recipient,       // 'vendor' | 'client'
  defaultEmail,
  recipientName,
  docNumber,        // orderNumber
  docUrl,
  companyName,
  onClose,
  onSent,
}) {
  const userId = localStorage.getItem('userId');
  const recipientLabel = recipient === 'vendor' ? 'Vendor' : 'Client';
  const notificationType = recipient === 'vendor' ? 'purchase-order-vendor' : 'purchase-order-client';
  const endpoint = recipient === 'vendor' ? 'send-purchase-order-vendor' : 'send-purchase-order-client';

  const [email, setEmail] = useState(defaultEmail || '');
  const [subject, setSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [primaryColor, setPrimaryColor] = useState(DEFAULTS.primaryColor);
  const [buttonTextColor, setButtonTextColor] = useState(DEFAULTS.buttonTextColor);
  const [signatureText, setSignatureText] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [messageTemplates, setMessageTemplates] = useState([]);

  const [activeTab, setActiveTab] = useState('compose');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    async function loadBranding() {
      try {
        const res = await fetch(`${API_BASE}/api/settings/${userId}`);
        const data = await res.json();
        setPrimaryColor(data.emailBranding?.primaryColor || DEFAULTS.primaryColor);
        setButtonTextColor(data.emailBranding?.buttonTextColor || DEFAULTS.buttonTextColor);
        setSignatureText(data.emailBranding?.signatureText || '');
        setLogoUrl(data.company?.logoUrl || '');
        setMessageTemplates(Array.isArray(data.messageTemplates) ? data.messageTemplates : []);
      } catch (err) {
        console.error('Failed to load email branding', err);
      }
    }
    if (userId) loadBranding();
  }, [userId]);

  const handleInsertTemplate = (e) => {
    const tpl = messageTemplates.find((t) => t.id === e.target.value);
    if (tpl) setCustomMessage(tpl.body);
    e.target.value = '';
  };

  const fetchPreview = useCallback(async () => {
    setPreviewing(true);
    try {
      const res = await authFetch(`${API_BASE}/api/notifications/preview-email`, {
        method: 'POST',
        body: JSON.stringify({
          type: notificationType,
          clientName: recipientName,
          docNumber,
          docUrl,
          companyName,
          customMessage,
          primaryColor,
          buttonTextColor,
          signatureText,
          logoUrl,
        }),
      });
      const data = await res.json();
      setPreviewHtml(data.html || '');
      if (!subject && data.subject) setSubject(data.subject);
      setActiveTab('preview');
    } catch (err) {
      console.error('Preview error', err);
      notify('Failed to load email preview.');
    } finally {
      setPreviewing(false);
    }
  }, [notificationType, recipientName, docNumber, docUrl, companyName, customMessage, primaryColor, buttonTextColor, signatureText, logoUrl, subject]);

  const handleSend = async () => {
    if (!email) return;
    setSending(true);
    try {
      const res = await authFetch(`${API_BASE}/api/notifications/${endpoint}`, {
        method: 'POST',
        body: JSON.stringify({
          toAddress: email,
          clientName: recipientName,
          poNumber: docNumber,
          poUrl: docUrl,
          companyName,
          subject,
          customMessage,
          primaryColor,
          buttonTextColor,
          signatureText,
          logoUrl,
        }),
      });

      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to send');
      }

      notify(`Purchase order sent to ${email}`);
      onSent?.();
      onClose();
    } catch (err) {
      console.error('Send email error', err);
      notify('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleOverlayClick = () => {
    if (!sending) onClose();
  };

  const defaultSubjectPlaceholder = docNumber
    ? recipient === 'vendor'
      ? `Purchase Order #${docNumber} from ${companyName || 'your studio'}`
      : `Order Update: #${docNumber} from ${companyName || 'your studio'}`
    : recipient === 'vendor'
      ? `Purchase Order from ${companyName || 'your studio'}`
      : `Order Update from ${companyName || 'your studio'}`;

  return (
    <div className="sem-overlay" onClick={handleOverlayClick}>
      <div className="sem-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sem-header">
          <h3>Send Purchase Order to {recipientLabel}</h3>
          <button className="sem-close" onClick={onClose} disabled={sending} aria-label="Close">
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="sem-tabs">
          <button
            className={activeTab === 'compose' ? 'sem-tab active' : 'sem-tab'}
            onClick={() => setActiveTab('compose')}
          >
            Compose
          </button>
          <button
            className={activeTab === 'preview' ? 'sem-tab active' : 'sem-tab'}
            onClick={previewHtml ? () => setActiveTab('preview') : fetchPreview}
            disabled={previewing}
          >
            {previewing ? 'Loading…' : 'Preview'}
          </button>
        </div>

        {/* Body */}
        <div className="sem-body">
          {activeTab === 'compose' && (
            <div className="sem-compose">
              <div className="sem-field">
                <label>To <span className="sem-optional">(separate multiple addresses with a comma)</span></label>
                <input
                  type="email"
                  multiple
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={recipient === 'vendor' ? 'vendor@example.com' : 'client@example.com'}
                  disabled={sending}
                />
              </div>

              <div className="sem-field">
                <label>Subject <span className="sem-optional">(leave blank for default)</span></label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={defaultSubjectPlaceholder}
                  disabled={sending}
                />
              </div>

              <div className="sem-field">
                <div className="sem-message-label-row">
                  <label>Message <span className="sem-optional">(leave blank for default)</span></label>
                  {messageTemplates.length > 0 && (
                    <select
                      className="sem-template-select"
                      defaultValue=""
                      onChange={handleInsertTemplate}
                      disabled={sending}
                    >
                      <option value="" disabled>Insert saved message…</option>
                      {messageTemplates.map((tpl) => (
                        <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <textarea
                  rows={4}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder={
                    recipient === 'vendor'
                      ? 'e.g. Hi! Please find our purchase order below. Let me know if you have any questions.'
                      : "e.g. Hi! Here's the latest status on your order."
                  }
                  disabled={sending}
                />
              </div>

              <div className="sem-field">
                <label>Signature / Closing <span className="sem-optional">(leave blank to use saved default)</span></label>
                <textarea
                  rows={3}
                  value={signatureText}
                  onChange={(e) => setSignatureText(e.target.value)}
                  placeholder={`e.g.\nBest regards,\nYour Studio Name`}
                  disabled={sending}
                />
              </div>

              <div className="sem-branding-section">
                <p className="sem-branding-title">Email Branding</p>
                <div className="sem-color-grid">
                  <div className="sem-color-field">
                    <label>Header &amp; Button Color</label>
                    <div className="sem-color-row">
                      <input
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="sem-color-swatch"
                      />
                      <input
                        type="text"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        maxLength={7}
                        className="sem-color-hex"
                      />
                    </div>
                  </div>
                  <div className="sem-color-field">
                    <label>Button Text Color</label>
                    <div className="sem-color-row">
                      <input
                        type="color"
                        value={buttonTextColor}
                        onChange={(e) => setButtonTextColor(e.target.value)}
                        className="sem-color-swatch"
                      />
                      <input
                        type="text"
                        value={buttonTextColor}
                        onChange={(e) => setButtonTextColor(e.target.value)}
                        maxLength={7}
                        className="sem-color-hex"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="sem-preview">
              {previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="sem-preview-frame"
                  sandbox="allow-same-origin"
                />
              ) : (
                <p className="sem-preview-empty">Click "Preview" to see how the email will look.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sem-footer">
          <button className="if-btn-secondary" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button
            className="if-btn-ghost"
            onClick={fetchPreview}
            disabled={sending || previewing}
          >
            {previewing ? 'Loading…' : 'Refresh Preview'}
          </button>
          <button
            className="if-btn-primary"
            onClick={handleSend}
            disabled={!email || sending}
          >
            {sending ? 'Sending…' : `Send to ${recipientLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
